import {
  Api,
  Code,
  Debug,
  DocumentMultiple_01,
  PillsAdd
} from '@carbon/icons-react'
import React, { useEffect, useState } from 'react'

import BillingCard from '../../../../../../../../components/New/BillingCardOrganization/BillingCard/BillingCard'
import { DDSTypography } from 'den-design-system'
import { Feature } from '../../../../../../../../enum/Feature.enum'
import PlanCard from '../../../../../../../../components/New/BillingCardOrganization/PlanCard/PlanCard'
import { ProjectType } from '../../../../../../../../enum/ProjectType.enum'
import Spinner from '../../../../../../../../components/common/Spinner/Spinner'
import StringConstants from '../../../../../../../../constants/StringConstants'
import { ToastMessageConstants } from '../../../../../../../../constants/ToastMessageConstants'
import ToastNotification from '../../../../../../../../components/common/DDS/Toast/Toast'
import { findAllMembers } from '../../../../../../../../api/organization/Organization.service'
import { getOrganizationUsageReport } from '../../../../../../../../api/subscription/Subscription.service'
import { getStripeCustomerPortalUrl } from '../../../../../../../../api/stripe/stripe.service'
import moment from 'moment'
import pallete from '../../../../../../../../global/newPallete'
import { replaceUrl } from '../../../../../../../../utils/UrlUtils'
import routeConstants from '../../../../../../../../constants/RouteConstants'
import { useNavigate } from 'react-router-dom'
import { useStoreContext } from '../../../../../../../../store/StoreContext'

interface BillingOverviewProps {
  isPlanDetailsLoading: any
  stripePlanData: any
  userLoading: any
  organizationDetail: any
  subscriptionDetails: any
}

const BillingOverview: React.FC<BillingOverviewProps> = ({
  userLoading,

  stripePlanData,
  isPlanDetailsLoading,
  organizationDetail,
  subscriptionDetails
}) => {
  const store = useStoreContext()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [featureUsageData, setFeatureUsageData] = useState<any>([])
  const [stripeCustomerPortalUrl, setStripeCustomerPortalUrl] =
    useState<string>()
  const [userCount, setUserCount] = useState<number | null>(
    store.userStore.getOrganizationMemberTotalCount() ?? null
  )
  const [usageLoading, setUsageLoading] = useState<boolean>(false)

  const fetchStripeDetails = () => {
    store.uiStore.setGlobalLoader(true)
    setIsLoading(true)
    getStripeCustomerPortalUrl({
      $id: organizationDetail?.stripeCustomerId,
      $organizationId: organizationDetail?.id
    })
      .then((response: any) => {
        setStripeCustomerPortalUrl(response.data.message)
      })
      .catch((e: any) => {
        ToastNotification({
          type: 'error',
          message: ToastMessageConstants.STRIPE
        })
      })
      .finally(() => {
        setIsLoading(false)
        store.uiStore.setGlobalLoader(false)
      })
  }

  const featureConfig = [
    {
      key: 'apiMonitoring',
      heading: 'API Monitoring',
      icon: <Api color={pallete.colors.stroke1} size={16} />
    },
    {
      key: 'exception',
      heading: 'Exceptions',
      icon: <Debug color={pallete.colors.stroke1} size={16} />
    },
    {
      key: 'incident',
      heading: 'Incident',
      icon: <DocumentMultiple_01 color={pallete.colors.stroke1} size={16} />
    },
    {
      key: 'jobMonitoring',
      heading: 'Jobs Monitoring',
      icon: <Code color={pallete.colors.stroke1} size={16} />
    },
    {
      key: 'applicationHealthCheck',
      heading: 'Uptime Checks',
      icon: <PillsAdd color={pallete.colors.stroke1} size={16} />
    }
  ]

  const getVisibleFeatureConfig = (data?: { type?: string }) => {
    const updatedConfig = featureConfig.map((feature) => {
      if (feature.key === 'exception') {
        let heading = 'Exceptions'

        if (data?.type === 'SALESFORCE_ECOSYSTEM_ONLY') {
          heading = 'Errors'
        }

        return { ...feature, heading }
      }
      return feature
    })

    // Hide apiMonitoring & jobMonitoring for SALESFORCE_ECOSYSTEM_ONLY projects
    if (data?.type === 'SALESFORCE_ECOSYSTEM_ONLY') {
      return updatedConfig.filter(
        (feature) =>
          feature.key !== 'apiMonitoring' && feature.key !== 'jobMonitoring'
      )
    }

    return updatedConfig
  }

  const mapOrganizationDetailToCards = (featureUsageData: any[]) => {
    const data = store.userStore.getSelectedOrganization()
    const visibleConfig = getVisibleFeatureConfig(data)

    return visibleConfig.map((cfg) => {
      const featureData =
        featureUsageData.find((item) => item.feature === cfg.key) || {}

      let totalValue: number | string = featureData.maxLimit ?? 0
      if (totalValue === -1) totalValue = 'unlimited'

      const isMonthlyFeatures = ['apiMonitoring', 'exception', 'incident']
      const monthly = isMonthlyFeatures.includes(cfg.key)
      const oneTime = !monthly

      return {
        heading: cfg.heading,
        value: featureData.active ?? 0,
        totalValue,
        icon: cfg.icon,
        monthly,
        oneTime
      }
    })
  }

  // ✅ Usage
  const cards = mapOrganizationDetailToCards(featureUsageData)

  useEffect(() => {
    setUsageLoading(true)
    getOrganizationUsageReport()
      .then((response: any) => {
        const featureArray = [
          Feature.USER,
          Feature.PROJECT,
          Feature.ENVIRONMENT
        ]
        const filteredData = response.data.usageData.filter((element: any) => {
          if (!featureArray.includes(element['feature'])) return element
        })
        setFeatureUsageData(filteredData)
      })
      .catch((error) => {
        console.log('Error while fetching organization usage report :: ', error)
        if (error.data.statusCode !== 400)
          ToastNotification({
            type: 'error',
            message: ToastMessageConstants.SUBSCRIPTION.GET_ORGANIZATION_USAGE
          })
      })
      .finally(() => {
        setUsageLoading(false)
      })
  }, [store.userStore.getOrganizations()])

  const escalationActiveCount =
    featureUsageData.find((item: any) => item.feature === 'escalation')
      ?.active ?? 0

  useEffect(() => {
    if (stripeCustomerPortalUrl) {
      window.location.href = stripeCustomerPortalUrl
    }
  }, [stripeCustomerPortalUrl])

  const projectLength = store.scopeStore.getProjects()?.length

  useEffect(() => {
    async function loadUserCount() {
      if (!userCount) {
        try {
          const organizationData: any = await findAllMembers({
            'expand-user': true
          })
          const count = organizationData.totalCount ?? 0
          setUserCount(count)
        } catch (err) {
          console.error('Failed to fetch members', err)
          setUserCount(0)
        }
      }
    }
    if (!store.userStore.getOrganizationMemberTotalCount()) {
      loadUserCount()
    }
  }, [])

  return (
    <>
      {isLoading || usageLoading || isPlanDetailsLoading ? (
        <div className='flex justify-center items-center h-[50vh]'>
          <Spinner />
        </div>
      ) : !isLoading || !usageLoading || !isPlanDetailsLoading ? (
        <div className='flex flex-col pt-[18px] '>
          {!organizationDetail?.isStripeCustomer ? (
            <PlanCard
              planName='Enterprise Plan'
              billingType='Monthly billing'
              price={'Custom'}
              isCurrentPlan={true}
              existingUser={true}
              users={userCount}
              projects={projectLength}
              escalations={escalationActiveCount}
              dataRetention={organizationDetail?.subscription?.dataRetention}
              buttonText='Increase Limit'
              onUpgrade={() => {
                window.location.href =
                  'mailto:support@vigilnow.com?subject= Vigil - Enterprise Plan Inquiry'
              }}
            />
          ) : stripePlanData?.planType ===
              StringConstants.STRIPE.ENTERPRISE_PLAN_TYPE &&
            organizationDetail?.isStripeCustomer &&
            organizationDetail?.subscription?.status === 'active' &&
            !stripePlanData?.cancelled ? (
            <PlanCard
              planName='Enterprise Plan'
              billingType='Monthly billing'
              renewDate={moment(
                organizationDetail?.subscription?.nextBillingAt
              ).format('DD MMM YYYY')}
              price={`$${stripePlanData?.priceAmountUsd}`}
              isCurrentPlan={true}
              users={userCount}
              projects={projectLength}
              escalations={escalationActiveCount}
              dataRetention={organizationDetail?.subscription?.dataRetention}
              buttonText='Increase Limit'
              secondButtonText={
                organizationDetail?.subscription?.status === 'active'
                  ? 'Change Payment Details'
                  : 'Subscribe'
              }
              onUpgrade={() => {
                window.location.href =
                  'mailto:support@vigilnow.com?subject= Vigil - Enterprise Plan Inquiry'
              }}
              onChangePayment={() => fetchStripeDetails()}
            />
          ) : stripePlanData?.planType ===
              StringConstants.STRIPE.ENTERPRISE_PLAN_TYPE &&
            organizationDetail?.isStripeCustomer &&
            organizationDetail?.subscription?.status === 'active' &&
            stripePlanData?.cancelled ? (
            <PlanCard
              planName='Enterprise Plan'
              billingType='Monthly billing'
              renewDate={stripePlanData?.cancelledDate}
              price={`$${stripePlanData?.priceAmountUsd}`}
              isCurrentPlan={true}
              isCancelledScheduled={true}
              users={userCount}
              projects={projectLength}
              escalations={escalationActiveCount}
              dataRetention={organizationDetail?.subscription?.dataRetention}
            />
          ) : stripePlanData?.planType ===
              StringConstants.STRIPE.ENTERPRISE_PLAN_TYPE &&
            organizationDetail?.isStripeCustomer &&
            organizationDetail?.subscription?.status === 'trialing' ? (
            <PlanCard
              planName='Enterprise Plan'
              billingType='Monthly billing'
              renewDate={moment(
                organizationDetail?.subscription?.nextBillingAt
              ).format('DD MMM YYYY')}
              existingUser={true}
              isEnterpriseNewUser={true}
              price={`$${stripePlanData?.priceAmountUsd}`}
              users={userCount}
              projects={projectLength}
              escalations={escalationActiveCount}
              dataRetention={organizationDetail?.subscription?.dataRetention}
              buttonText='Subscribe to the plan'
              onUpgrade={() => fetchStripeDetails()}
            />
          ) : organizationDetail?.subscription?.status === 'trialing' ? (
            <PlanCard
              planName='Trial period'
              renewDate={moment(
                organizationDetail?.subscription?.nextBillingAt
              ).format('DD MMM YYYY')}
              users={userCount}
              projects={projectLength}
              escalations={escalationActiveCount}
              dataRetention={organizationDetail?.subscription?.dataRetention}
              isTrialExpired={true}
              buttonText='Choose plan'
              onUpgrade={() =>
                navigate(
                  replaceUrl(
                    routeConstants.SUBSCRIPTION_PLAN,
                    store.scopeStore.getOrganizationId() as string,
                    '',
                    ''
                  )
                )
              }
            />
          ) : organizationDetail?.subscription?.status === 'canceled' ? (
            <PlanCard
              planName='Basic Plan'
              billingType='Monthly billing'
              renewDate={moment(
                organizationDetail?.subscription?.cancelledAt
              ).format('DD MMM YYYY')}
              price={`$${stripePlanData?.priceAmountUsd}`}
              isCancelled={true}
              isCurrentPlan={false}
              users={userCount}
              projects={projectLength}
              escalations={escalationActiveCount}
              dataRetention={organizationDetail?.subscription?.dataRetention}
              buttonText='Re - Subscribe'
              secondButtonText='Choose Plan'
              onUpgrade={() => fetchStripeDetails()}
              onChangePayment={() =>
                navigate(
                  replaceUrl(
                    routeConstants.SUBSCRIPTION_PLAN,
                    store.scopeStore.getOrganizationId() as string,
                    '',
                    ''
                  )
                )
              }
            />
          ) : organizationDetail?.subscription?.status === 'past_due' ? (
            <PlanCard
              planName='Trial period'
              renewDate={moment(
                organizationDetail?.subscription?.nextBillingAt
              ).format('DD MMM YYYY')}
              users={userCount}
              projects={projectLength}
              escalations={escalationActiveCount}
              dataRetention={organizationDetail?.subscription?.dataRetention}
              isPastDue={true}
              isTrialExpired={true}
              buttonText='Choose plan'
              onUpgrade={() =>
                navigate(
                  replaceUrl(
                    routeConstants.SUBSCRIPTION_PLAN,
                    store.scopeStore.getOrganizationId() as string,
                    '',
                    ''
                  )
                )
              }
            />
          ) : stripePlanData?.planType ===
              StringConstants.STRIPE.BASIC_PLAN_TYPE &&
            organizationDetail?.isStripeCustomer &&
            organizationDetail?.subscription?.status === 'active' &&
            stripePlanData?.cancelled ? (
            <PlanCard
              planName='Basic Plan'
              billingType='Monthly billing'
              renewDate={stripePlanData?.cancelledDate}
              price={`$${stripePlanData?.priceAmountUsd}`}
              isCurrentPlan={true}
              isCancelledScheduled={true}
              users={userCount}
              projects={projectLength}
              escalations={escalationActiveCount}
              dataRetention={organizationDetail?.subscription?.dataRetention}
            />
          ) : stripePlanData?.planType ===
              StringConstants.STRIPE.BASIC_PLAN_TYPE &&
            organizationDetail?.isStripeCustomer &&
            organizationDetail?.subscription?.status === 'active' &&
            !stripePlanData?.cancelled ? (
            <PlanCard
              planName='Basic Plan'
              billingType='Monthly billing'
              renewDate={moment(
                organizationDetail?.subscription?.nextBillingAt
              ).format('DD MMM YYYY')}
              price={`$${stripePlanData?.priceAmountUsd}`}
              isCurrentPlan={true}
              users={userCount}
              projects={organizationDetail?.projectCount}
              escalations={escalationActiveCount}
              dataRetention={organizationDetail?.subscription?.dataRetention}
              onUpgrade={() =>
                navigate(
                  replaceUrl(
                    routeConstants.SUBSCRIPTION_PLAN,
                    store.scopeStore.getOrganizationId() as string,
                    '',
                    ''
                  )
                )
              }
              onChangePayment={() => fetchStripeDetails()}
            />
          ) : stripePlanData?.planType ===
              StringConstants.STRIPE.BASIC_PLAN_TYPE &&
            organizationDetail?.isStripeCustomer &&
            organizationDetail?.subscription?.status === 'incomplete' ? (
            <PlanCard
              planName='Basic Plan'
              billingType='Monthly billing'
              isIncomplete={true}
              price={`$${stripePlanData?.priceAmountUsd}`}
              users={userCount}
              projects={organizationDetail?.projectCount}
              escalations={escalationActiveCount}
              buttonText='Re-Subscribe'
              dataRetention={organizationDetail?.subscription?.dataRetention}
              onUpgrade={() => fetchStripeDetails()}
            />
          ) : stripePlanData?.planType ===
              StringConstants.STRIPE.ENTERPRISE_PLAN_TYPE &&
            organizationDetail?.isStripeCustomer &&
            organizationDetail?.subscription?.status === 'incomplete' ? (
            <PlanCard
              planName='Enterprise Plan'
              billingType='Monthly billing'
              isIncomplete={true}
              price={`$${stripePlanData?.priceAmountUsd}`}
              users={userCount}
              projects={projectLength}
              escalations={escalationActiveCount}
              buttonText='Re-Subscribe'
              dataRetention={organizationDetail?.subscription?.dataRetention}
              onUpgrade={() => fetchStripeDetails()}
            />
          ) : (
            <PlanCard
              planName='Basic Plan'
              billingType='Monthly billing'
              renewDate={moment(
                organizationDetail?.subscription?.nextBillingAt
              ).format('DD MMM YYYY')}
              price={`$${stripePlanData?.priceAmountUsd}`}
              isCurrentPlan={true}
              users={userCount}
              projects={organizationDetail?.projectCount}
              escalations={escalationActiveCount}
              dataRetention={organizationDetail?.subscription?.dataRetention}
              onUpgrade={() =>
                navigate(
                  replaceUrl(
                    routeConstants.SUBSCRIPTION_PLAN,
                    store.scopeStore.getOrganizationId() as string,
                    '',
                    ''
                  )
                )
              }
              onChangePayment={() => fetchStripeDetails()}
            />
          )}

          <div className='mt-[24px] mb-[16px]'>
            <DDSTypography.Title
              type='h5'
              variant='semiBold'
              color={pallete.colors.textDark3}
            >
              Usage Overview
            </DDSTypography.Title>
          </div>
          {/* First row */}
          <div>
            <div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 2xl:grid-cols-4 gap-[24px]'>
              {cards.map((card, index) => (
                <BillingCard
                  key={index}
                  heading={card.heading}
                  value={card.value}
                  totalValue={card.totalValue}
                  icon={card.icon}
                  monthly={card.monthly}
                  oneTime={card.oneTime}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className='flex justify-center items-center h-[50vh]'>
          <Spinner />
        </div>
      )}
    </>
  )
}

export default BillingOverview
